import React from 'react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge"
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { ArrowLeft, Download, Loader2, Save } from 'lucide-react';
import { CheckCircle2 } from 'lucide-react';
import { generateAppraisalPDF } from '@/lib/pdfExport';
import { toast } from "sonner";

const stepIndexFn = (STEPS, status) => {
  const i = STEPS.indexOf(status);
  return i >= 0 ? i : 0;
};

const StepIndicator = ({ currentStatus, STEPS }) => {
  const currentIdx = stepIndexFn(STEPS, currentStatus);
  const isAllDone = currentStatus === 'Completed';

  return (
    <div className="flex items-center gap-0 py-2" data-testid="appraisal-step-indicator">
      {STEPS.map((step, i) => {
        const isCompleted = isAllDone || i < currentIdx;
        const isCurrent = !isAllDone && i === currentIdx;

        return (
          <div key={step} className="flex items-center">
            <div className="flex flex-col items-center gap-1.5 shrink-0 min-w-[92px]">
              <div className={`h-9 w-9 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-all duration-200 ${isCompleted
                ? 'bg-primary border-primary text-primary-foreground shadow-sm shadow-primary/30'
                : isCurrent
                  ? 'bg-primary/15 border-primary text-primary ring-4 ring-primary/10 scale-105'
                  : 'bg-card border-border text-muted-foreground'
                }`}>
                {isCompleted ? <CheckCircle2 className="h-5 w-5" /> : i + 1}
              </div>
              <span className={`text-[11px] text-center leading-tight tracking-tight ${isCurrent ? 'text-primary font-semibold' : isCompleted ? 'text-foreground/80 font-medium' : 'text-muted-foreground'
                }`}>
                {step}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`h-1 w-12 rounded-full -mt-5 shrink-0 transition-colors ${isCompleted ? 'bg-primary' : 'bg-border/70'}`} />
            )}
          </div>
        );
      })}
    </div>
  );
};

const MiniStepDots = ({ currentStatus, STEPS }) => {
  const currentIdx = stepIndexFn(STEPS, currentStatus);
  const isAllDone = currentStatus === 'Completed';

  return (
    <div className="flex items-center gap-1.5" aria-hidden="true">
      {STEPS.map((step, i) => {
        const isCompleted = isAllDone || i < currentIdx;
        const isCurrent = !isAllDone && i === currentIdx;
        return (
          <span
            key={step}
            className={`block rounded-full transition-all ${isCurrent
              ? 'h-2 w-4 bg-primary'
              : isCompleted
                ? 'h-2 w-2 bg-primary'
                : 'h-2 w-2 bg-border'
              }`}
          />
        );
      })}
    </div>
  );
};

const getBadgeClass = () => {
  return 'border-primary/30 text-primary bg-primary/10 gap-1.5 py-1 px-2.5 font-medium';
};

function AppraisalHeader({
  appraisal,
  status,
  submitAction,
  draftAction,
  isCompleted,
  isEmployeeView,
  onBack,
  onExportPDF,
  submitting,
  STEPS,
  eyebrow,
  hidePdfExport,
  template,
  employee
}) {

  const handlePDFExport = async () => {
    const hasQuestions = template?.sections?.length > 0;
    
    if (!hasQuestions) {
      toast.error("No appraisal data available for PDF generation");
      return;
    }

    try {
      const reportData = {
        appraisal,
        template,
        employee,
        cycle: { name: appraisal.cycleName }
      };

      await generateAppraisalPDF(reportData);
      toast.success("PDF downloaded successfully");
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to generate PDF");
    }
  };

  const backLabel = isEmployeeView ? 'Back to Reviews' : 'Back to Cycles';
  const hasSteps = Array.isArray(STEPS) && STEPS.length > 0;
  const currentIdx = hasSteps ? stepIndexFn(STEPS, status) : 0;
  const totalSteps = hasSteps ? STEPS.length : 0;
  const stepPositionLabel = status === 'Completed' ? 'Completed' : `Step ${currentIdx + 1} of ${totalSteps}`;
  const cycleName = appraisal.cycleName || 'Appraisal';
  const draftSavedLabel = draftAction?.savedAt
    ? `Draft saved ${new Date(draftAction.savedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`
    : null;

  const StatusBadge = (
    <Badge variant="outline" className={`text-xs dark:border-muted-foreground/40 ${getBadgeClass()}`}>
      {status}
    </Badge>
  );

  return (
    <Card className="mb-6 overflow-hidden shadow-sm">
      <CardContent className="p-4 sm:p-6 via-card to-card">
        {/* MOBILE DESIGN (< 640px) */}
        <div className="sm:hidden space-y-3">
          {/* Row 1: Back link + compact step indicator */}
          <div className="flex flex-col items-start justify-between gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={onBack}
              data-testid="back-to-cycles"
              className="h-8 px-2 -ml-2 text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4 mr-1.5" />
              {backLabel}
            </Button>
            {hasSteps && (
              <div className="w-fit flex items-center gap-2 rounded-full bg-muted/60 px-2.5 py-1">
                <MiniStepDots currentStatus={status} STEPS={STEPS} />
                <span className="text-[11px] font-semibold text-muted-foreground">
                  {stepPositionLabel}
                </span>
              </div>
            )}
          </div>

          {/* Row 2: Eyebrow + title + status */}
          <div className="space-y-1.5">
            {eyebrow && (
              <p className="text-[10px] uppercase tracking-wider font-semibold text-primary/80">
                {eyebrow}
              </p>
            )}
            <h1 className="text-lg font-bold leading-tight dark:text-white" data-testid="appraisal-title">
              <span className="line-clamp-2" title={`${cycleName} — ${appraisal.employeeName}`}>
                {cycleName} — {appraisal.employeeName}
              </span>
            </h1>
            <div className="pt-0.5">{StatusBadge}</div>
          </div>

          <Separator />

          {/* Row 3: Actions */}
          <div className="flex flex-col gap-2">
            {draftSavedLabel && (
              <p className="text-xs text-muted-foreground text-center" data-testid="draft-saved-label">{draftSavedLabel}</p>
            )}
            {submitAction && !isCompleted && (
              <Button
                className={`${submitAction.color} h-10 text-sm w-full shadow-sm`}
                onClick={submitAction.handler}
                disabled={submitting || submitAction.disabled || draftAction?.saving}
                data-testid="submit-step-btn"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <submitAction.icon className="h-4 w-4 mr-2" />}
                {submitAction.label}
              </Button>
            )}
            {draftAction && !isCompleted && (
              <Button
                variant="outline"
                onClick={draftAction.handler}
                disabled={submitting || draftAction.saving}
                data-testid="save-draft-btn"
                className="h-10 text-sm w-full"
              >
                {draftAction.saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                Save Draft
              </Button>
            )}
            {!hidePdfExport && (
              <Button
                variant="default"
                onClick={handlePDFExport}
                data-testid="export-pdf-btn"
                className="h-10 text-sm w-full"
              >
                <Download className="h-4 w-4 mr-1" />
                Download PDF
              </Button>
            )}
          </div>
        </div>

        {/* TABLET & DESKTOP DESIGN (≥ 640px) */}
        <div className="hidden sm:block">
          <div className="flex items-start gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={onBack}
              data-testid="back-to-cycles"
              className="shrink-0 rounded-full hover:bg-primary/10 hover:text-primary"
              aria-label={backLabel}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex-1 min-w-0">
              {eyebrow && (
                <p className="text-[11px] uppercase tracking-wider font-semibold text-primary/80">
                  {eyebrow}
                </p>
              )}
              <h1 className="text-2xl dark:text-white font-bold flex items-center gap-3 mt-0.5" data-testid="appraisal-title">
                <span className="truncate" title={`${cycleName} — ${appraisal.employeeName}`}>
                  {cycleName} — {appraisal.employeeName}
                </span>
                <span className="flex">{StatusBadge}</span>
              </h1>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {!hidePdfExport && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePDFExport}
                  data-testid="export-pdf-btn"
                  className="hover:bg-primary/5 hover:text-primary hover:border-primary/40 dark:border-muted-foreground/40 dark:hover:border-primary/30 dark:hover:bg-primary/10 dark:hover:text-primary"
                >
                  <Download className="mr-2 h-4 w-4" /> PDF
                </Button>
              )}
              {draftSavedLabel && (
                <span className="text-xs text-muted-foreground whitespace-nowrap" data-testid="draft-saved-label">{draftSavedLabel}</span>
              )}
              {draftAction && !isCompleted && (
                <Button
                  variant="outline"
                  onClick={draftAction.handler}
                  disabled={submitting || draftAction.saving}
                  data-testid="save-draft-btn"
                  className="gap-2 hover:bg-primary/5 hover:text-primary hover:border-primary/40 dark:border-muted-foreground/40 dark:hover:border-primary/30 dark:hover:bg-primary/10 dark:hover:text-primary"
                >
                  {draftAction.saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Save Draft
                </Button>
              )}
              {submitAction && !isCompleted && (
                <Button
                  className={`${submitAction.color} gap-2 shadow-sm`}
                  onClick={submitAction.handler}
                  disabled={submitting || submitAction.disabled || draftAction?.saving}
                  data-testid="submit-step-btn"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <submitAction.icon className="h-4 w-4" />}
                  {submitAction.label}
                </Button>
              )}
            </div>
          </div>

          {hasSteps && (
            <>
              <Separator className="my-4" />

              {/* Step Indicator */}
              <div className="flex justify-center">
                <StepIndicator currentStatus={status} STEPS={STEPS} />
              </div>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default AppraisalHeader;
